import heroImage from "./article_pics/suricata_bench_tile.jpg";
const article = {
    title: "Suricata-Bench: A Practical Workflow for Checking ET Open Coverage and Testing Custom Rules",
    published: "2026-04-30",
    readingTime: "8 min read",
    tags: ["Suricata", "Detection Engineering", "PCAP", "Threat Hunting", "Docker"],
    summary: "Suricata-Bench gives me a fast way to validate whether a PCAP is already covered by ET Open and, if not, to iteratively test custom Suricata rules against the same traffic until the detection behaves the way I want.",
    favorite: false,
    image: heroImage,
    repoUrl: "https://github.com/Pb-22/Suricata-Bench",
    content: `
# Suricata-Bench: A Practical Workflow for Checking ET Open Coverage and Testing Custom Rules

When I am working through a new detection idea, one of the first questions I want answered is simple: does a rule already exist for this? If I have a PCAP that represents suspicious or interesting activity, I do not want to immediately jump into writing a custom signature if ET Open already covers the behavior well enough. At the same time, if nothing fires, I want a fast way to move directly into testing my own rule without changing tools or rebuilding the workflow from scratch.

That was the motivation behind Suricata-Bench. I wanted a practical testing surface where I could bring in a PCAP, check for existing ET Open coverage, and then pivot straight into custom rule development if needed.

## What Suricata-Bench Does

Suricata-Bench is a simple workflow tool for offline Suricata testing against PCAPs. It lets me upload traffic, choose whether to run ET Open, provide a custom rule if I want one, and then inspect the results.

The value is not just that it runs Suricata. The value is that it supports the way I actually work when I am evaluating detections.

Suricata-Bench is also ready to go with Docker Compose, which makes it easier to stand up the required pieces and get straight to testing through a local web UI rather than assembling the workflow manually each time. You can check out the project here: [Suricata-Bench on GitHub](https://github.com/Pb-22/Suricata-Bench).

## My Default Workflow

This is the main way I use Suricata-Bench.

### 1. Load a PCAP and test ET Open first

I start by bringing in a PCAP and checking the ET Open box while leaving the custom rule area blank. This gives me a quick answer to a very important question: is the behavior I care about already detected by existing community coverage?

If ET Open fires in a useful way, that may be enough. Sometimes that means the idea is already covered and I do not need to spend time writing something new. Sometimes it means I still want something more specific, but at least I know what the baseline looks like.

### 2. If nothing fires, move to a custom rule

If ET Open does not alert on the traffic in a useful way, I then start writing a custom rule. At that point I uncheck the ET Open box and focus only on the rule I am developing.

This helps isolate the test. I am no longer asking whether public rules detect the activity. I am asking whether my rule detects the exact thing I care about in the PCAP.

### 3. Use errors as part of the rule-writing loop

If the rule has a syntax problem, parser problem, or some other issue, Suricata-Bench shows the errors. That matters because rule development is usually iterative. A rule may be conceptually right but still fail because the buffer choice is wrong, the syntax is off, or the logic needs to be simplified.

Instead of treating that as friction, I use it as part of the workflow. I read the error output, adjust the rule, and run it again. That loop continues until the rule both loads cleanly and detects what I intended.

![Suricata-Bench local web UI](${require("./article_pics/bench.png")})

*Suricata-Bench provides a local web UI for uploading PCAPs, testing ET Open coverage, supplying custom rules, and reviewing alerts or rule errors in one place.*

## Other Useful Cases

Another use case is validating a PCAP when I already expect Suricata to alert on it. If I believe a known rule should fire, Suricata-Bench gives me a quick way to confirm that the traffic actually produces the expected alert.

It is also useful when building PCAPs for training, lab work, or exercises. If I am creating traffic for an exercise or classroom scenario, I may want to confirm ahead of time that participants will receive meaningful Suricata alerts that can drive the next stage of investigation. In that sense, the tool is not just for rule creation — it is also useful for validating whether a PCAP will behave the way I intend in a learning or operational scenario.

## Why This Workflow Helps

What I like about this approach is that it keeps the work grounded.

It helps answer practical questions such as:

- Does ET Open already detect this PCAP?
- If not, can I write something targeted that does?
- Is my rule syntactically valid?
- Does the rule actually match the traffic I care about?
- Do I need to simplify the logic before making it more specific?

That makes Suricata-Bench useful not just for testing finished rules, but for shaping detection ideas early.

## A Good Fit for Detection Development

This workflow fits how I prefer to build detections in general. I like to prove the concept first, then refine. In Suricata terms, that often means starting with a simple, parser-safe rule that confirms I am matching the right part of the traffic. Once that works, I can add narrower conditions and improve fidelity.

Suricata-Bench makes that process easier because I can stay focused on the detection logic itself rather than spending time reassembling the test environment every time I want to validate a change.

## Final Thoughts

For me, Suricata-Bench is most useful as a decision tool and a validation loop. It tells me whether I already have coverage through ET Open, and if I do not, it gives me a straightforward way to test custom rules against real traffic and improve them through iteration.

That is the kind of workflow I want when I am moving from an interesting PCAP to a usable detection.
`
};

export default article;
